/**
 * useSelectOptionFallback — guarantee every editable dropdown has options.
 *
 * Wraps `withSelectFallbacks` with the app's option sources, in order:
 *
 *   1. `pick_lists` for this (table, field) — the managed vocabulary that the
 *      server normally inlines into `/metadata` as `options`. Re-reading it
 *      here covers the case where the metadata row says "managed pick list"
 *      but the values were never seeded in this database, and the case where
 *      a surface renders a table whose lookup key it never wired.
 *   2. A dedicated legacy endpoint for the handful of vocabularies that still
 *      have one. `deals.deal_status` is the live example: migration 083 moves
 *      it from the `deal_status` table into `pick_lists`, but KEEPS the table
 *      and `GET /deal_status` (the deal_status_history metadata rows still
 *      resolve through it). Whichever half of that migration a given database
 *      has, one of these two sources answers.
 *
 * Both endpoints are `ALL_BD`, so every broker-deals role — external broker
 * agents included — can read them.
 *
 * Returns the same objects it was given when nothing needed patching, so the
 * caller's memo identity is stable and the LookupSelects don't re-fetch.
 */
import { useCallback, useContext, useMemo } from "react";

import { AppContext } from "../AppContextProvider";
import dealsService from "../services/dealsService";
import pickListsService from "../services/pickListsService";
import { filterLookupOptions } from "../utils/lookupFilter";
import { withSelectFallbacks } from "../utils/selectOptionFallback";

/**
 * Vocabularies that predate `pick_lists` and still have their own endpoint.
 * Keyed `<table>.<field>`; each returns `{ id, label }` options.
 */
const DEDICATED_SOURCES = {
  "deals.deal_status": async ({ token, userOid }) => {
    const resp = await dealsService.listDealStatuses({ token, userOid });
    const rows = resp?.data?.data || resp?.data || [];
    return Array.isArray(rows)
      ? rows
          .map((r) => r?.deal_status ?? r?.value ?? r)
          .filter((v) => v != null && v !== "")
          .map((v) => ({ id: v, label: String(v) }))
      : [];
  },
};

/**
 * @param {Array<object>} metadata UI metadata rows for one table
 * @param {Record<string, Function>} lookupFetchers the surface's fetchers
 * @param {string} tableName the metadata table (e.g. "deals")
 * @returns {{ metadata: Array<object>, lookupFetchers: Record<string, Function> }}
 */
export const useSelectOptionFallback = (metadata, lookupFetchers, tableName) => {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const resolve = useCallback(
    async (row, query) => {
      const auth = await getAuth();
      let opts = [];
      try {
        const resp = await pickListsService.listPickLists({
          ...auth,
          tableName: row?.table_name || tableName,
          field: row?.field,
        });
        const rows = resp?.data?.data || resp?.data || [];
        // The stored value IS the option (these vocabularies are flat enums),
        // so id === value and the label is cosmetic.
        opts = Array.isArray(rows)
          ? rows
              .map((r) => ({ value: r?.value ?? r, label: r?.label }))
              .filter((o) => o.value != null && o.value !== "")
              .map((o) => ({
                id: o.value,
                label: String(o.label || o.value),
              }))
          : [];
      } catch {
        // Fall through to the dedicated source; an empty dropdown is the
        // failure this hook exists to avoid, so never rethrow.
        opts = [];
      }

      if (opts.length === 0) {
        const dedicated =
          DEDICATED_SOURCES[`${row?.table_name || tableName}.${row?.field}`];
        if (dedicated) {
          try {
            opts = await dedicated(auth);
          } catch {
            opts = [];
          }
        }
      }
      return filterLookupOptions(opts, query);
    },
    [getAuth, tableName]
  );

  return useMemo(
    () => withSelectFallbacks(metadata, lookupFetchers, resolve),
    [metadata, lookupFetchers, resolve]
  );
};

export default useSelectOptionFallback;
